import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, Clock, AlertCircle, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Document Signing Modal - Embeds Zoho Sign iframe for in-platform signing
 */
export const DocumentSigningModal = ({ isOpen, onClose, investmentId, onSigningComplete }) => {
  const [signingUrl, setSigningUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMock, setIsMock] = useState(false);

  useEffect(() => {
    if (isOpen && investmentId) {
      fetchSigningUrl();
    }
  }, [isOpen, investmentId]);

  const fetchSigningUrl = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/investments/${investmentId}/sign-url`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.already_signed) {
        onSigningComplete?.();
        onClose();
        return;
      }
      
      if (data.is_mock) {
        setIsMock(true);
        setSigningUrl(null);
      } else if (data.signing_url) {
        setSigningUrl(data.signing_url);
      } else {
        setError('Unable to load signing interface. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching signing URL:', err);
      setError('Failed to load document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMockSign = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/investments/${investmentId}/complete-signing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        onSigningComplete?.();
        onClose();
      }
    } catch (err) {
      console.error('Error completing mock signing:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="document-signing-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Sign LLP Agreement</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
            data-testid="close-signing-modal"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading document...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchSigningUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : isMock ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <FileText className="w-16 h-16 text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Document Ready for Signature</h3>
              <p className="text-gray-600 mb-6 max-w-md">
                Your LLP Agreement has been prepared. In production, you would sign directly here.
                For now, click below to simulate signing.
              </p>
              <button
                onClick={handleMockSign}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                data-testid="mock-sign-button"
              >
                ✓ Sign Document (Demo)
              </button>
            </div>
          ) : signingUrl ? (
            <iframe
              src={signingUrl}
              className="w-full h-full border-0"
              title="Sign Document"
              data-testid="zoho-sign-iframe"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-600">Unable to load signing interface.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Investment Journey Tracker - Shows the investment lifecycle with document signing status
 */
export const InvestmentJourney = ({ investment, onSignDocument }) => {
  const state = investment?.state || investment?.status || 'UNKNOWN';
  
  const stages = [
    { key: 'EOI_PENDING', label: 'EOI Pending', icon: Clock },
    { key: 'EOI_PAID', label: 'EOI Paid', icon: CheckCircle },
    { key: 'DOC_SENT', label: 'Document Sent', icon: FileText },
    { key: 'DOC_SIGNED', label: 'Document Signed', icon: CheckCircle },
    { key: 'CAPITAL_PENDING', label: 'Capital Pending', icon: Clock },
    { key: 'ACTIVE', label: 'Active', icon: CheckCircle },
  ];

  const getCurrentStageIndex = () => {
    const stateMap = {
      'EOI_PENDING': 0,
      'EOI_PAID': 1,
      'DOC_SENT': 2,
      'DOC_SIGNED': 3,
      'CAPITAL_PENDING': 4,
      'CAPITAL_PAID': 4,
      'ACTIVE': 5,
      'COMPLETED': 5,
    };
    return stateMap[state] ?? 0;
  };

  const currentIndex = getCurrentStageIndex();
  const needsSignature = state === 'DOC_SENT';

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="investment-journey">
      <h3 className="text-lg font-semibold mb-4">Investment Progress</h3>
      
      {/* Progress Bar */}
      <div className="relative mb-6">
        <div className="flex justify-between">
          {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            
            return (
              <div key={stage.key} className="flex flex-col items-center relative z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`text-xs mt-2 text-center ${isCurrent ? 'font-semibold text-blue-600' : 'text-gray-500'}`}>
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Progress Line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-0">
          <div
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${(currentIndex / (stages.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Action Card for Document Signing */}
      {needsSignature && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4" data-testid="sign-document-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900">LLP Agreement Ready</h4>
                <p className="text-sm text-blue-700">Please review and sign your agreement</p>
              </div>
            </div>
            <button
              onClick={onSignDocument}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              data-testid="sign-document-button"
            >
              <FileText className="w-4 h-4" />
              Sign Now
            </button>
          </div>
        </div>
      )}

      {/* Status Message */}
      <div className="mt-4 text-sm text-gray-600">
        {state === 'EOI_PAID' && 'Your EOI payment has been received. Document will be sent shortly.'}
        {state === 'DOC_SENT' && 'Your LLP Agreement is ready for signature.'}
        {state === 'DOC_SIGNED' && 'Document signed! Awaiting capital payment.'}
        {state === 'ACTIVE' && 'Your investment is active.'}
      </div>
    </div>
  );
};

export default { DocumentSigningModal, InvestmentJourney };
